"use client";

import { makeFunctionReference } from "convex/server";
import { useQuery } from "convex/react";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { getConsoleSnapshot } from "@/lib/demo-data";
import { InsightsView } from "./InsightsView";
import type { ManagerAnalytics } from "@/core/console/manager-analytics";

type Snapshot = ReturnType<typeof getConsoleSnapshot>;

const getDashboardSnapshot = makeFunctionReference<
  "query",
  { tenantId: string; limit?: number },
  Snapshot
>("dashboard:getSnapshot");

const getManagerAnalytics = makeFunctionReference<
  "query",
  { tenantId: string },
  ManagerAnalytics
>("analytics:getManagerAnalytics");

export function InsightsClient({ tenantId }: { tenantId: string }) {
  const fallback = getConsoleSnapshot();
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <InsightsView snapshot={fallback} />;
  }
  return <LiveInsights tenantId={tenantId} fallback={fallback} />;
}

function LiveInsights({
  tenantId,
  fallback,
}: {
  tenantId: string;
  fallback: Snapshot;
}) {
  const snapshot = useQuery(getDashboardSnapshot, { tenantId, limit: 50 });
  const analytics = useQuery(getManagerAnalytics, { tenantId });
  if (snapshot === undefined) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <QuerySkeleton surface="consoleDashboard" />
      </div>
    );
  }
  return <InsightsView snapshot={snapshot ?? fallback} analytics={analytics ?? undefined} />;
}
